/* Verono — kinetic-motion.js (Stage 5D.3; 5D.4 C+D card controller and footer behaviour; 5D.4.4 card controller rewritten: Product / In use selector + preview-once; 5D.4.5 hidden-until-eligible selector, fail-closed withdrawal, seen-on-cancel, zoom gate)
   One section-scoped controller for: one-shot scroll reveals, the proof-rail video behaviour and Shopify Theme Editor events.
   Progressive enhancement: server HTML is fully visible; this script only hides elements that are still BELOW the viewport
   when it arms, then reveals each once. Reduced motion, Save-Data and the Theme Editor (design mode) get no automatic motion. */
(function () {
  'use strict';
  if (window.__kineticMotion) return;
  window.__kineticMotion = true;

  var doc = document;
  var design = !!(window.Shopify && window.Shopify.designMode);
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var hasIO = 'IntersectionObserver' in window;
  function reduce() { return mqReduce.matches; }
  /* 5D.4.6: slow-connection and zoom gates. Both only ever make MORE content
     show immediately, so they cannot hide anything. */
  function slowNet() {
    var c = navigator.connection;
    if (!c || !c.effectiveType) return false;
    return c.effectiveType === '2g' || c.effectiveType === 'slow-2g';
  }
  function zoomed() {
    var vv = window.visualViewport;
    return !!(vv && vv.scale && vv.scale > 1.5);
  }
  function noAutoMotion() { return reduce() || saveData || design || slowNet() || zoomed() || !hasIO; }

  /* Stage 5D.4.9 — decorative entrance motion is DESKTOP ONLY.
     Below 990px every decorative reveal target renders immediately at opacity 1 /
     transform none: it is never handed to the IntersectionObserver, never given a
     pending class, and never arms the scroll safety sweep. That removes the choppy
     mobile entrance animation at source rather than trying to smooth it.
     Functional motion (Product/In-use crossfade, drawers, menus, dialogs, filters,
     accordions, video, variant controls, Quick Add, buttons, loading feedback) is
     untouched — this gate only guards armReveals. */
  var mqDesktop = window.matchMedia ? window.matchMedia('(min-width: 990px)') : { matches: true };
  function desktopViewport() { return !!mqDesktop.matches; }
  /* Merchant settings arrive as CSS custom properties emitted by layout/theme.liquid
     (Theme settings -> Verono scroll animations), so no inline script is needed.
     Read once and cached: these cannot change without a reload. Any unreadable value
     falls back to the shipped default, and the CSS guard in theme.liquid independently
     forces content visible, so a failure here can never leave anything hidden. */
  var cfgCache = null;
  function cfg() {
    if (cfgCache) return cfgCache;
    var v = { desktop: true, mobile: false, leadM: 0 };
    try {
      var cs = getComputedStyle(doc.documentElement);
      var d = cs.getPropertyValue('--kv-motion-desktop').trim();
      var m = cs.getPropertyValue('--kv-motion-mobile').trim();
      var ld = parseInt(cs.getPropertyValue('--kv-reveal-lead-m'), 10);
      if (d === '0') v.desktop = false;
      if (m === '1') v.mobile = true;
      if (!isNaN(ld) && ld >= 0 && ld <= 30) v.leadM = ld;
    } catch (e) { /* keep defaults */ }
    cfgCache = v;
    return v;
  }
  function viewportAllowed() {
    var c = cfg();
    return desktopViewport() ? c.desktop : c.mobile;
  }
  function decorativeAllowed() { return viewportAllowed() && !noAutoMotion(); }

  /* ---------------- Reveals ---------------- */
  var revealIO = null;
  var STAGGER_CAP = 6;

  function showNow(el) {
    el.classList.remove('kv-pre', 'kv-in');
    el.setAttribute('data-kv-state', 'shown');
  }
  function revealEl(el) {
    if (el.getAttribute('data-kv-state') === 'shown') return;
    el.setAttribute('data-kv-state', 'shown');
    el.classList.add('kv-in');
    var done = function () { el.classList.remove('kv-pre', 'kv-in'); el.style.removeProperty('--kv-delay'); el.removeEventListener('transitionend', done); };
    el.addEventListener('transitionend', done);
    setTimeout(done, 1200);
  }
  /* The observer is viewport-specific. Desktop keeps the original inset trigger,
     which reads well on a large slow-scrolling screen. Mobile extends the root box
     below the fold by the merchant's "reveal start point" (0–30% of the screen);
     at 0 a reveal starts the moment an element reaches the bottom edge, so the
     whole animation is seen as it scrolls in. (6 Sep 2026: the old 20–30% floor
     meant phone reveals usually finished off-screen — Joe could not see them.) */
  var revealIOMode = null;
  var revealIOSpanH = 0;
  /* The observer root is extended upward by the DOCUMENT's own scroll height, so
     any element above the viewport is inside the root by construction — no magic
     constant. An anchor jump or fast fling that carries a pending element past
     the fold between two observer frames therefore still ends inside the root
     and the observer itself reveals it. This replaces the old scroll/rAF sweep —
     there is deliberately NO scroll listener in the reveal system. armReveals
     re-checks the span on every arming pass, so a page that grows taller gets a
     rebuilt observer (pending elements are re-observed below). */
  function revealRootSpan() {
    var a = doc.documentElement ? doc.documentElement.scrollHeight : 0;
    var b = doc.body ? doc.body.scrollHeight : 0;
    var v = (window.innerHeight || 0) * 2;
    return Math.max(a || 0, b || 0, v || 0, 1000);
  }
  function ensureRevealIO() {
    if (!hasIO) return;
    var mode = desktopViewport() ? 'd' : 'm';
    var span = revealRootSpan();
    if (revealIO && revealIOMode === mode && span <= revealIOSpanH) return;
    var rebuilt = !!revealIO;
    if (revealIO) revealIO.disconnect();
    revealIOMode = mode;
    revealIOSpanH = span;
    var opts = mode === 'd'
      ? { rootMargin: span + 'px 0px -6% 0px', threshold: 0.06 }
      : { rootMargin: span + 'px 0px ' + cfg().leadM + '% 0px', threshold: 0 };
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        revealIO.unobserve(e.target);
        revealEl(e.target);
      });
    }, opts);
    /* A rebuild drops every existing observation, so anything still pending has to
       be handed to the new observer or it would stay hidden forever. */
    if (rebuilt) {
      Array.prototype.forEach.call(doc.querySelectorAll('.kv-pre'), function (el) { revealIO.observe(el); });
    }
  }
  function armReveals(scope) {
    var sections = scope.matches && scope.matches('[data-kv-section]') ? [scope] : scope.querySelectorAll('[data-kv-section]');
    Array.prototype.forEach.call(sections, function (section) {
      /* a hidden marker section may ask for a one-shot reveal of Dawn's collection grid items (initial discovery only;
         nodes replaced later by Dawn's AJAX filtering are never observed, so nothing replays or stays hidden) */
      var items;
      if (section.hasAttribute('data-kv-cards-reveal')) {
        items = doc.querySelectorAll('#product-grid > .grid__item:not([data-kv-state])');
      } else if (section.hasAttribute('data-kv-pdp-reveal')) {
        items = pdpRevealTargets(section);
      } else {
        items = section.querySelectorAll('[data-kv-reveal]:not([data-kv-state])');
      }
      var cardsMode = section.hasAttribute('data-kv-cards-reveal');
      var off = section.getAttribute('data-kv-motion') === 'off' || !decorativeAllowed();
      if (off) { Array.prototype.forEach.call(items, showNow); return; }
      ensureRevealIO();
      /* Desktop keeps each section's own Stagger control. Below 990px a group
         always arrives together (0ms stagger) — the capped mobile preset — so a
         phone gets one consistent, calm rhythm. Documented in Theme settings ->
         Verono scroll animations. */
      var step = desktopViewport()
        ? parseInt(section.getAttribute('data-kv-stagger') || '50', 10)
        : 0;
      if (isNaN(step) || step < 0) step = 0;
      var vh = window.innerHeight || doc.documentElement.clientHeight;
      var groupIndex = {};
      Array.prototype.forEach.call(items, function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) { showNow(el); return; }          /* already on screen: never hidden */
        if (r.bottom <= 0) { showNow(el); return; }                       /* above the viewport: never hidden */
        var g = el.getAttribute('data-kv-group') || (cardsMode ? 'cards' : 'solo');
        var idx = groupIndex[g] = (groupIndex[g] || 0) + 1;
        if (g !== 'solo') el.style.setProperty('--kv-delay', (Math.min(idx - 1, STAGGER_CAP) * step) + 'ms');
        el.setAttribute('data-kv-state', 'pre');
        el.classList.add('kv-pre');
        revealIO.observe(el);
      });
    });
  }
  /* 5D.4.6 — Product-page appear-on-scroll target inventory.
     Deliberately an ALLOWLIST, never a blanket .shopify-section sweep. A section
     qualifies only when it: belongs to the product template (id starts with
     "template--", which excludes the shared header/footer groups), sits AFTER the
     main product section in DOM order, and matches one of the curated static
     content types below. The main product section (LCP media, title, rating,
     price, variant controls, quantity, Add to cart, accelerated checkout and the
     sticky ATC), the header, footer, announcement bar, promo popup, newsletter
     form and every opaque app/review container are all excluded. */
  var PDP_TYPES = ['multirow_', 'rich_text_', 'collapsible_content_', 'before_after_slider_', 'ds_horizontal_ticker_', 'image_banner_', 'blocks_'];
  function pdpRevealTargets(marker) {
    /* :has() is not universally supported, so resolve the main product section
       from the product-info element itself. */
    var pi = doc.querySelector('product-info, [id^="MainProduct"], .product__info-container');
    var mainSection = pi && pi.closest ? pi.closest('.shopify-section') : null;
    var all = Array.prototype.slice.call(doc.querySelectorAll('.shopify-section'));
    var startIdx = mainSection ? all.indexOf(mainSection) : -1;
    if (startIdx < 0) return [];
    var out = [];
    for (var i = startIdx + 1; i < all.length; i++) {
      var s = all[i];
      if (s.hasAttribute('data-kv-state')) continue;
      var id = (s.id || '').replace(/^shopify-section-/, '');
      if (id.indexOf('template--') !== 0) continue;           /* shared header/footer groups excluded */
      var rest = id.slice(id.indexOf('__') + 2);
      var ok = false;
      for (var k = 0; k < PDP_TYPES.length; k++) { if (rest.indexOf(PDP_TYPES[k]) === 0) { ok = true; break; } }
      if (!ok) continue;                                       /* opaque hex-id app/review sections excluded */
      if (s.querySelector('dialog, [role="dialog"], form[action*="/contact"], .newsletter-form')) continue;
      out.push(s);
    }
    return out;
  }

  function revealAllPending() {
    Array.prototype.forEach.call(doc.querySelectorAll('.kv-pre'), function (el) { if (revealIO) revealIO.unobserve(el); revealEl(el); });
  }
  /* Safety nets: nothing may stay hidden if an observer never fires. */
  function safetySweep() {
    var vh = window.innerHeight || doc.documentElement.clientHeight;
    Array.prototype.forEach.call(doc.querySelectorAll('.kv-pre'), function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh + 40 && r.bottom > -40) { if (revealIO) revealIO.unobserve(el); revealEl(el); }
    });
  }
  /* The old 5D.4 scroll/rAF sweep is gone: the reveal system now runs on ONE
     shared IntersectionObserver whose root extends 20000px above the viewport,
     which covers the fling/anchor-jump case the sweep existed for. bfcache
     restores and the one-shot safety timers below stay as the fail-open net. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) revealAllPending(); });
  /* 5D.4.6 — a pending wrapper must never swallow keyboard focus. Capture phase so
     the element is revealed BEFORE focus is painted. */
  doc.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var pending = t.closest('.kv-pre');
    if (pending) { if (revealIO) revealIO.unobserve(pending); showNow(pending); }
  }, true);
  /* 5D.4.6 — deep-linked (#hash) content reveals immediately, never animated. */
  function revealHashTarget() {
    var h = location.hash;
    if (!h || h.length < 2) return;
    var el;
    try { el = doc.querySelector(h); } catch (err) { return; }
    if (!el) return;
    var pending = el.closest ? el.closest('.kv-pre') : null;
    if (pending) { if (revealIO) revealIO.unobserve(pending); showNow(pending); }
    Array.prototype.forEach.call(el.querySelectorAll ? el.querySelectorAll('.kv-pre') : [], function (p) {
      if (revealIO) revealIO.unobserve(p); showNow(p);
    });
  }
  window.addEventListener('hashchange', revealHashTarget);
  if (mqReduce.addEventListener) mqReduce.addEventListener('change', function () { if (reduce()) revealAllPending(); });
  /* Exactly one viewport listener. Crossing DOWN to below 990px releases and unobserves
     anything still pending so nothing can stay hidden. Crossing back UP never re-hides
     content that is already visible — revealAllPending only ever makes things visible. */
  if (mqDesktop.addEventListener) {
    mqDesktop.addEventListener('change', function (e) {
      /* Show everything the moment we land on a side where reveals are switched off,
         so crossing the breakpoint can never strand a pending element. Otherwise
         rebuild the observer for the new viewport's trigger geometry. */
      var c = cfg();
      if (e.matches ? !c.desktop : !c.mobile) { revealAllPending(); return; }
      ensureRevealIO();
    });
  }

  /* ---------------- Proof rail (video) ---------------- */
  var rails = [];
  function Rail(section) {
    this.el = section;
    this.autoplay = section.getAttribute('data-kv-autoplay') !== 'false' && !reduce() && !saveData && !design;
    this.threshold = parseFloat(section.getAttribute('data-kv-threshold') || '0.55');
    this.pauseBelow = parseFloat(section.getAttribute('data-kv-pause') || '0.2');
    this.items = Array.prototype.slice.call(section.querySelectorAll('[data-kr-item]'));
    this.ratios = new Map();
    this.userPaused = new WeakSet();
    this.current = null;
    this.sourced = false;
    this.bound = [];
    this.init();
  }
  Rail.prototype.on = function (target, type, fn, opts) { target.addEventListener(type, fn, opts); this.bound.push([target, type, fn, opts]); };
  Rail.prototype.init = function () {
    var self = this;
    this.items.forEach(function (item) {
      var v = item.querySelector('video'); if (!v) return;
      v.muted = true; v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.playsInline = true;
      v.removeAttribute('controls');
      var play = item.querySelector('[data-kr-play]');
      var mute = item.querySelector('[data-kr-mute]');
      var retry = item.querySelector('[data-kr-retry]');
      if (play) self.on(play, 'click', function () { self.toggle(item, true); });
      if (retry) self.on(retry, 'click', function () { self.userPaused.delete(item); self.playItem(item, true, true); });
      if (mute) self.on(mute, 'click', function () {
        v.muted = !v.muted; mute.setAttribute('aria-pressed', v.muted ? 'false' : 'true');
        mute.setAttribute('aria-label', (v.muted ? mute.getAttribute('data-label-unmute') : mute.getAttribute('data-label-mute')) || '');
        item.classList.toggle('kr-item--sound', !v.muted);
      });
      self.on(v, 'play', function () { self.setState(item, true); });
      self.on(v, 'pause', function () { self.setState(item, false); });
      self.on(v, 'ended', function () { self.setState(item, false); if (!v.loop) { try { v.currentTime = 0; } catch (e) {} } });
      self.on(v, 'error', function () { self.fail(item, 'error'); });
      self.setState(item, false);
    });
    if (!hasIO) { this.injectSources(); return; }
    this.approachIO = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) { self.injectSources(); self.approachIO.disconnect(); }
    }, { rootMargin: '400px 0px' });
    this.approachIO.observe(this.el);
    this.visIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { self.ratios.set(e.target, e.intersectionRatio); if (e.intersectionRatio < self.pauseBelow) self.userPaused.delete(e.target); });
      self.evaluate();
    }, { threshold: [0, 0.1, 0.2, 0.4, 0.55, 0.6, 0.75, 1] });
    this.items.forEach(function (item) { self.visIO.observe(item); });
    this.onVis = function () { if (doc.hidden) self.pauseAll(); else self.evaluate(); };
    this.on(doc, 'visibilitychange', this.onVis);
  };
  Rail.prototype.injectSources = function () {
    if (this.sourced) return; this.sourced = true;
    this.items.forEach(function (item) {
      var v = item.querySelector('video'); if (!v || !v.getAttribute('data-src')) return;
      var src = v.getAttribute('data-src'), srcM = v.getAttribute('data-src-mobile');
      if (srcM && window.matchMedia && window.matchMedia('(max-width: 749px)').matches) src = srcM;
      var s = doc.createElement('source'); s.src = src; s.type = v.getAttribute('data-type') || 'video/mp4';
      /* preload stays "none" and load() is NOT called: Safari would otherwise fetch whole files at approach.
         Bytes flow only when a clip actually starts (play()). */
      v.appendChild(s);
      item.classList.add('kr-item--ready');
    });
  };
  Rail.prototype.setState = function (item, playing) {
    var play = item.querySelector('[data-kr-play]');
    item.classList.toggle('kr-item--playing', !!playing);
    if (play) {
      play.setAttribute('aria-pressed', playing ? 'true' : 'false');
      play.setAttribute('aria-label', (playing ? play.getAttribute('data-label-pause') : play.getAttribute('data-label-play')) || '');
    }
    if (playing) { this.current = item; item.classList.remove('kr-item--failed'); }
    else if (this.current === item) this.current = null;
  };
  Rail.prototype.pauseAll = function (except) {
    this.items.forEach(function (item) { if (item === except) return; var v = item.querySelector('video'); if (v && !v.paused) v.pause(); });
  };
  Rail.prototype.playItem = function (item, byUser, isRetry) {
    var self = this, v = item.querySelector('video'); if (!v) return;
    this.injectSources();
    this.pauseAll(item);
    var p;
    try { p = v.play(); } catch (e) { p = Promise.reject(e); }
    if (p && p.then) p.then(function () { item.classList.remove('kr-item--failed'); }).catch(function (err) {
      /* an autoplay attempt interrupted by a later pause()/load() is not a failure */
      if (err && err.name === 'AbortError') return;
      if (!byUser) { self.setState(item, false); return; }   /* autoplay refused: stay on the poster with the normal Play control */
      self.fail(item, isRetry ? 'retry' : 'first');
    });
  };
  Rail.prototype.fail = function (item, stage) {
    var v = item.querySelector('video');
    item.classList.add('kr-item--failed');
    this.setState(item, false);
    /* second genuine failure of the custom path: expose native controls as the final recovery route */
    if (stage === 'retry' || stage === 'error') { if (v) v.setAttribute('controls', 'controls'); item.classList.add('kr-item--native'); }
  };
  Rail.prototype.toggle = function (item) {
    var v = item.querySelector('video'); if (!v) return;
    if (v.paused) { this.userPaused.delete(item); this.playItem(item, true); }
    else { this.userPaused.add(item); v.pause(); }
  };
  Rail.prototype.evaluate = function () {
    if (!this.autoplay || doc.hidden) return;
    var self = this, best = null, bestRatio = 0;
    this.items.forEach(function (item) {
      var r = self.ratios.get(item) || 0, v = item.querySelector('video');
      if (!v) return;
      if (!v.paused && r < self.pauseBelow) v.pause();
      if (r >= self.threshold && r > bestRatio && !self.userPaused.has(item) && !item.classList.contains('kr-item--failed')) { best = item; bestRatio = r; }
    });
    if (best && best !== this.current) this.playItem(best, false);
  };
  Rail.prototype.focusItem = function (blockId) {
    var item = this.items.filter(function (i) { return i.getAttribute('data-kr-block') === blockId; })[0];
    if (item && item.scrollIntoView) item.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
  };
  Rail.prototype.destroy = function () {
    this.pauseAll();
    if (this.approachIO) this.approachIO.disconnect();
    if (this.visIO) this.visIO.disconnect();
    this.bound.forEach(function (b) { b[0].removeEventListener(b[1], b[2], b[3]); });
    this.bound = [];
  };
  function railFor(section) { return rails.filter(function (r) { return r.el === section; })[0]; }
  function initRails(scope) {
    var nodes = scope.matches && scope.matches('[data-kv-rail]') ? [scope] : scope.querySelectorAll('[data-kv-rail]');
    Array.prototype.forEach.call(nodes, function (section) { if (!railFor(section)) rails.push(new Rail(section)); });
  }
  function destroyRails(scope) {
    rails = rails.filter(function (r) { if (scope === r.el || scope.contains(r.el)) { r.destroy(); return false; } return true; });
  }


  /* ---------------- C+D product cards (Stage 5D.4 → rewritten 5D.4.4) ----------------
     One idempotent controller for every opt-in card (.card-wrapper.cd-card with a [data-cd-view] selector).
     States per card:  view: product|in_use   editorial: unrequested|loading|ready|failed
                       auto: disabled|armed|running|complete|cancelled   manual: false|true
     Events are delegated once at document level (change/click on the selector, pointer hover on fine pointers);
     one shared IntersectionObserver handles automatic eligibility. Nothing is re-bound per render, so Dawn AJAX
     replacement, bfcache restores and Theme Editor section load/unload/reorder never duplicate listeners,
     observers, timers or requests. Zero editorial bytes until intent in every mode. */
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };
  var coarsePointer = window.matchMedia ? window.matchMedia('(hover: none), (pointer: coarse)') : { matches: false };
  var CD_VIS = 0.75, CD_DWELL = 1200, CD_FADE = 220, CD_HOLD = 2200, CD_HOVER_INTENT = 120;
  var CD_KEY = 'verono:editorial-previewed:v1:';
  var cdStates = typeof WeakMap === 'function' ? new WeakMap() : null;
  var cdLive = [];                 /* list of live wrappers (for sweeps) */
  var cdLock = null;               /* the one card that may automatically request/animate */
  var cdIO = null;                 /* shared IntersectionObserver */
  var cdSeenMem = {};              /* fallback when sessionStorage is unavailable */
  var cdLastScroll = 0;
  var cdLiveRegion = null;

  function cdSlowNet() {
    var c = navigator.connection; if (!c) return false;
    if (c.saveData === true) return true;
    var t = (c.effectiveType || '').toLowerCase(); return t === 'slow-2g' || t === '2g';
  }
  /* 5D.4.5: a pinch/page zoom above ~150 % also holds automatic behaviour back (manual switching stays available) */
  function cdZoomed() { try { return !!(window.visualViewport && window.visualViewport.scale > 1.5); } catch (err) { return false; } }
  function cdAutoAllowed() { return coarsePointer.matches && !reduce() && !cdSlowNet() && !cdZoomed() && hasIO; }
  function cdSeen(pid) { try { if (window.sessionStorage && sessionStorage.getItem(CD_KEY + pid)) return true; } catch (err) { /* storage unavailable */ } return !!cdSeenMem[pid]; }
  function cdMarkSeen(pid) { cdSeenMem[pid] = true; try { if (window.sessionStorage) sessionStorage.setItem(CD_KEY + pid, '1'); } catch (err) { /* ignore */ } }
  function cdAnnounce(text) {
    if (!cdLiveRegion) { cdLiveRegion = doc.createElement('div'); cdLiveRegion.className = 'visually-hidden'; cdLiveRegion.setAttribute('aria-live', 'polite'); cdLiveRegion.setAttribute('aria-atomic', 'true'); cdLiveRegion.setAttribute('data-cd-live', ''); doc.body.appendChild(cdLiveRegion); }
    cdLiveRegion.textContent = ''; setTimeout(function () { cdLiveRegion.textContent = text; }, 50);
  }
  function cdGet(wrapper) { return cdStates ? cdStates.get(wrapper) : wrapper.__cd; }
  function cdState(wrapper) {
    var st = cdGet(wrapper); if (st) return st;
    var media = wrapper.querySelector('[data-cd-media]'); var view = wrapper.querySelector('[data-cd-view]'); var ed = media && media.querySelector('.cd-img--editorial');
    if (!media || !view || !ed || !ed.getAttribute('data-cd-src')) return null;   /* not eligible: no selector, no editorial → nothing to control */
    st = { wrapper: wrapper, media: media, view: view, ed: ed, inputs: view.querySelectorAll('[data-cd-view-input]'), pid: view.getAttribute('data-cd-product') || '', mode: view.getAttribute('data-cd-mode') || 'manual',
           view_state: 'product', editorial: 'unrequested', auto: 'disabled', manual: false, dwell: null, hold: null, hover: null, ratio: 0, announced: false, loadPromise: null };
    if (cdStates) cdStates.set(wrapper, st); else wrapper.__cd = st;
    cdLive.push(wrapper); wrapper.setAttribute('data-cd-init', '1');
    view.hidden = false;                       /* 5D.4.5: the selector is only revealed once the card is confirmed eligible */
    return st;
  }
  function cdSetRadio(st, inUse) { Array.prototype.forEach.call(st.inputs, function (i) { i.checked = (i.value === 'in_use') === !!inUse; }); }
  function cdPaint(st, inUse) {
    st.view_state = inUse ? 'in_use' : 'product';
    st.media.classList.toggle('cd-media--active', !!inUse);
    cdSetRadio(st, inUse);
  }
  /* Request the editorial exactly once; resolves true when decoded, false on failure. Never retries after failure. */
  function cdRequest(st) {
    if (st.editorial === 'ready') return Promise.resolve(true);
    if (st.editorial === 'failed') return Promise.resolve(false);
    if (st.loadPromise) return st.loadPromise;
    st.editorial = 'loading'; st.media.setAttribute('aria-busy', 'true');
    var img = st.ed;
    st.loadPromise = new Promise(function (resolve) {
      var done = function (ok) { st.media.removeAttribute('aria-busy'); if (ok) { st.editorial = 'ready'; resolve(true); } else { cdFail(st); resolve(false); } };
      img.addEventListener('load', function () { if (img.decode) img.decode().then(function () { done(true); }, function () { done(true); }); else done(true); }, { once: true });
      img.addEventListener('error', function () { done(false); }, { once: true });
      img.setAttribute('sizes', img.getAttribute('data-cd-sizes') || '');
      img.setAttribute('srcset', img.getAttribute('data-cd-srcset') || '');
      img.setAttribute('src', img.getAttribute('data-cd-src') || '');
      img.setAttribute('data-cd-loaded', '1');
    });
    return st.loadPromise;
  }
  /* Fail closed (5D.4.5): the studio image stays, the selector is withdrawn entirely (no inert control), one announcement. */
  function cdFail(st) {
    st.editorial = 'failed'; cdPaint(st, false); cdAutoFinish(st, 'cancelled');
    Array.prototype.forEach.call(st.inputs, function (i) { i.checked = i.value === 'product'; });
    st.view.hidden = true; st.view.classList.add('cd-view--unavailable');
    if (!st.announced) { st.announced = true; cdAnnounce('In-use image unavailable'); }
  }
  /* Manual selection (either mode): the shopper owns the card from here on. */
  function cdSelect(st, inUse) {
    st.manual = true; cdAutoFinish(st, 'cancelled');
    if (!inUse) { cdPaint(st, false); return; }
    if (st.editorial === 'failed') { cdPaint(st, false); return; }
    cdSetRadio(st, true);
    cdRequest(st).then(function (ok) { if (!ok) return; var want = Array.prototype.some.call(st.inputs, function (i) { return i.value === 'in_use' && i.checked; }); if (want) cdPaint(st, true); });
  }
  /* Fine-pointer hover / keyboard focus preview (unchanged behaviour): transient unless the shopper selected manually. */
  function cdHoverEnter(st) {
    if (st.manual) return;
    clearTimeout(st.hover);
    st.hover = setTimeout(function () { if (st.manual) return; cdRequest(st).then(function (ok) { if (ok && !st.manual && st.hoverOn) cdPaint(st, true); }); }, CD_HOVER_INTENT);
    st.hoverOn = true;
  }
  function cdHoverLeave(st) { clearTimeout(st.hover); st.hoverOn = false; if (!st.manual) cdPaint(st, false); }

  /* ---- automatic preview once (coarse pointers only) ---- */
  function cdReleaseLock(st) { if (cdLock === st) cdLock = null; }
  /* Ends the automatic life of a card for this page view. A running preview returns to Product (unless the shopper has
     pinned In use) and is recorded for the session; the card is unobserved, so automatic behaviour never resumes. */
  function cdAutoFinish(st, result) {
    clearTimeout(st.dwell); clearTimeout(st.hold); st.dwell = null; st.hold = null;
    if (st.auto === 'running' && st.view_state === 'in_use' && !st.manual) cdPaint(st, false);
    /* 5D.4.5: once an attempt has armed or begun for this product, any completion OR cancellation is recorded for the
       session, so a reload or Dawn AJAX replacement can never re-arm it. A deliberate manual choice always wins. */
    if (st.auto === 'running' || st.auto === 'armed') { cdMarkSeen(st.pid); st.auto = result; }
    cdReleaseLock(st);
    if (cdIO) cdIO.unobserve(st.wrapper);
  }
  function cdEligible(st) {
    return st.auto === 'armed' && !st.manual && cdAutoAllowed() && st.ratio >= CD_VIS && !doc.hidden && !st.wrapper.contains(doc.activeElement) && !cdLock && !cdSeen(st.pid) && st.wrapper.isConnected;
  }
  /* ≥75 % visible for 1200 ms AND no scroll event in the last 1200 ms ("settled"); re-checks until both hold. */
  function cdScheduleDwell(st) {
    clearTimeout(st.dwell);
    if (st.auto !== 'armed' || st.ratio < CD_VIS) return;
    st.dwell = setTimeout(function () { st.dwell = null; if (st.auto !== 'armed' || st.ratio < CD_VIS) return; if (Date.now() - cdLastScroll < CD_DWELL) { cdScheduleDwell(st); return; } cdRun(st); }, CD_DWELL);
  }
  function cdRun(st) {
    if (!cdEligible(st)) return;                 /* lock busy / touched / seen → stays armed; a NEW settled entry is needed to try again */
    cdLock = st; st.auto = 'running';
    cdRequest(st).then(function (ok) {
      if (!ok || st.auto !== 'running' || cdLock !== st) { cdAutoFinish(st, 'cancelled'); return; }
      if (st.ratio < CD_VIS || doc.hidden || st.manual || st.wrapper.contains(doc.activeElement)) { cdAutoFinish(st, 'cancelled'); return; }
      cdPaint(st, true);
      st.hold = setTimeout(function () { if (st.auto === 'running' && !st.manual) { cdPaint(st, false); } cdAutoFinish(st, 'complete'); }, CD_FADE + CD_HOLD);
    });
  }
  function cdArm(st) {
    if (st.mode !== 'preview_once' || !cdAutoAllowed() || cdSeen(st.pid) || st.manual || st.editorial === 'failed') { st.auto = 'disabled'; return; }
    st.auto = 'armed';
    if (!cdIO) {
      cdIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var st2 = cdGet(e.target); if (!st2) return;
          st2.ratio = e.intersectionRatio;
          if (e.intersectionRatio >= CD_VIS) { if (st2.auto === 'armed') cdScheduleDwell(st2); }
          else { clearTimeout(st2.dwell); st2.dwell = null; if (st2.auto === 'running') cdAutoFinish(st2, 'cancelled'); }
        });
      }, { threshold: [0, CD_VIS, 1] });
    }
    cdIO.observe(st.wrapper);
  }
  /* Any touch or focus on the card ends its automatic life (the shopper's intent always wins). */
  function cdTouched(wrapper) { var st = cdGet(wrapper); if (!st) return; if (st.auto === 'armed' || st.auto === 'running') cdAutoFinish(st, 'cancelled'); }
  function initCards(scope) {
    var wrappers = scope.querySelectorAll ? scope.querySelectorAll('.cd-card:not([data-cd-init])') : [];
    Array.prototype.forEach.call(wrappers, function (w) { var st = cdState(w); if (st) cdArm(st); });
  }
  function cdTeardown(scope) {
    cdLive = cdLive.filter(function (w) {
      var st = cdGet(w); var gone = !w.isConnected || (scope && scope !== doc && scope.contains(w));
      if (gone && st) { clearTimeout(st.dwell); clearTimeout(st.hold); clearTimeout(st.hover); cdReleaseLock(st); if (cdIO) cdIO.unobserve(w); }
      return !gone;
    });
  }
  /* delegated events — bound exactly once */
  doc.addEventListener('change', function (e) {
    var input = e.target; if (!input || !input.hasAttribute || !input.hasAttribute('data-cd-view-input')) return;
    var wrapper = input.closest('.cd-card'); var st = wrapper && cdState(wrapper); if (!st) return;
    e.stopPropagation(); cdSelect(st, input.value === 'in_use');
  });
  doc.addEventListener('click', function (e) {
    var fs = e.target.closest ? e.target.closest('[data-cd-view]') : null; if (!fs) return;
    e.stopPropagation();
    if (!e.target.closest('label, input')) e.preventDefault();   /* dead zone of the fieldset: never fall through to the card link */
  });
  doc.addEventListener('focusin', function (e) {
    var wrapper = e.target.closest ? e.target.closest('.cd-card') : null; if (!wrapper) return; var st = cdState(wrapper); if (!st) return;
    if (e.target.closest('[data-cd-view]')) { cdTouched(wrapper); return; }          /* focus in the selector cancels any automatic attempt */
    if (st.auto === 'running' || st.auto === 'armed') cdAutoFinish(st, 'cancelled');
    if (finePointer.matches && !st.manual) cdHoverEnter(st);
  });
  doc.addEventListener('focusout', function (e) {
    var wrapper = e.target.closest ? e.target.closest('.cd-card') : null; if (!wrapper) return; var st = cdGet(wrapper); if (!st) return;
    if (wrapper.contains(e.relatedTarget)) return;
    if (finePointer.matches) cdHoverLeave(st);
  });
  doc.addEventListener('pointerover', function (e) {
    if (!finePointer.matches || (e.pointerType && e.pointerType !== 'mouse')) return;
    var wrapper = e.target.closest ? e.target.closest('.cd-card') : null; if (!wrapper) return;
    if (e.relatedTarget && wrapper.contains(e.relatedTarget)) return;          /* moving within the same card */
    var st = cdState(wrapper); if (st) cdHoverEnter(st);
  });
  doc.addEventListener('pointerout', function (e) {
    if (!finePointer.matches || (e.pointerType && e.pointerType !== 'mouse')) return;
    var wrapper = e.target.closest ? e.target.closest('.cd-card') : null; if (!wrapper) return;
    if (e.relatedTarget && wrapper.contains(e.relatedTarget)) return;
    var st = cdGet(wrapper); if (st) cdHoverLeave(st);
  });
  doc.addEventListener('pointerdown', function (e) {
    if (e.pointerType === 'mouse') return;
    var wrapper = e.target.closest ? e.target.closest('.cd-card') : null; if (wrapper) cdTouched(wrapper);
  }, { passive: true });
  window.addEventListener('scroll', function () { cdLastScroll = Date.now(); }, { passive: true });
  doc.addEventListener('visibilitychange', function () { if (doc.hidden && cdLock && cdLock.auto === 'running') cdAutoFinish(cdLock, 'cancelled'); });
  window.addEventListener('pageshow', function (e) { if (e.persisted) { cdLive.forEach(function (w) { var st = cdGet(w); if (st && st.auto === 'running') cdAutoFinish(st, 'cancelled'); }); } });
  /* Dawn replaces the collection grid on AJAX filtering/sorting: tear down removed cards, initialise new ones (no replay) */
  var gridHost = doc.getElementById('ProductGridContainer');
  if (gridHost && 'MutationObserver' in window) {
    new MutationObserver(function () { cdTeardown(null); initCards(gridHost); }).observe(gridHost, { childList: true, subtree: true });
  }
  /* ---------------- Footer (Stage 5D.4): accordions below 750px, auto-submit localization, success focus ---------------- */
  var mqNarrow = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };
  function syncFooterAccordions() {
    Array.prototype.forEach.call(doc.querySelectorAll('[data-kf-acc]'), function (d) {
      if (mqNarrow.matches) { if (!d.hasAttribute('data-kf-user')) d.open = false; }
      else { d.open = true; }
    });
  }
  function initFooter(scope) {
    var footers = scope.querySelectorAll ? scope.querySelectorAll('.kf:not([data-kf-init])') : [];
    Array.prototype.forEach.call(footers, function (kf) {
      kf.setAttribute('data-kf-init', '1');
      Array.prototype.forEach.call(kf.querySelectorAll('[data-kf-acc]'), function (d) {
        d.addEventListener('toggle', function () { if (mqNarrow.matches) d.setAttribute('data-kf-user', '1'); });
      });
      Array.prototype.forEach.call(kf.querySelectorAll('[data-kf-localize]'), function (sel) {
        var form = sel.form; if (!form) return;
        form.classList.add('kf-local--js');
        sel.addEventListener('change', function () { form.submit(); });
      });
      var ok = kf.querySelector('[data-kf-success]');
      if (ok && /customer_posted=true/.test(location.search)) { try { ok.focus({ preventScroll: false }); } catch (err) { /* ignore */ } }
    });
    syncFooterAccordions();
  }
  if (mqNarrow.addEventListener) mqNarrow.addEventListener('change', syncFooterAccordions);
  /* ---------------- Boot + Theme Editor ---------------- */
  function init(scope) { armReveals(scope); initRails(scope); initCards(scope); initFooter(scope); }
  function boot() {
    /* 5D.4.6 — if initialisation throws for ANY reason, remove every pending
       state so no content can be left invisible. Fail open, always. */
    try {
      init(doc);
      revealHashTarget();
    } catch (err) {
      revealAllPending();
      Array.prototype.forEach.call(doc.querySelectorAll('.kv-pre'), showNow);
      return;
    }
    setTimeout(safetySweep, 1500);
    window.addEventListener('load', function () { setTimeout(safetySweep, 300); });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();

  doc.addEventListener('shopify:section:load', function (e) {
    try { init(e.target); } catch (err) { Array.prototype.forEach.call(doc.querySelectorAll('.kv-pre'), showNow); }
  });
  doc.addEventListener('shopify:section:unload', function (e) { destroyRails(e.target); cdTeardown(e.target); });
  doc.addEventListener('shopify:block:select', function (e) {
    var section = e.target.closest ? e.target.closest('[data-kv-rail]') : null;
    var rail = section && railFor(section);
    if (rail && e.detail && e.detail.blockId) rail.focusItem(e.detail.blockId);
  });
})();
